import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PreviewIcon from "../../../resources/icons/arrow-square-up-right.svg";
import Copy from "../../../resources/icons/copy.svg";
import correct from "../../../resources/icons/correct.svg";
import CopyToClipBoard from "../../../utils/CopyToClipBoard";
import CredentialPreviewModal from "../../../common/CredentialPreviewModal/CredentialPreviewModal";
import { formatLocalDateTime } from "../../../utils/formatLocalDateTime";
import { useTranslation } from "react-i18next";

const IssuedCredentialLongCard = (props) => {
  const {
    transaction_id,
    name,
    transaction_status,
    completed_at,
    updated_at,
    issuer,
    svg_url,
    pdf_url,
    hasAnyUrls,
    document_type,
    payment_status,
    verification_cost_overall,
    order_id,
    uploaded_document,
    mode_of_verification,
    certificate_id,
    verification_status,
    payment_id,
    programme_name,
    programme_code,
    recipient_name,
    recipient_email,
    issuer_logo,
    issuer_organisation,
    verification_cost,
    comments,
    payment_signature,
    org_code,
    razorpay_client_id,
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentCredentialId, setCurrentCredentialId] = useState(null);

  const openCloseModal = (transaction_id) => {
    if (isOpen && currentCredentialId === transaction_id) {
      setIsOpen(false);
      setCurrentCredentialId(null);
    } else {
      setIsOpen(true);
      setCurrentCredentialId(transaction_id);
    }
  };

  const handleCopyClick = (event, id) => {
    event.stopPropagation();
    CopyToClipBoard(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadPDF = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(t("transaction:failed_fetch_file"));
      }

      const disposition = response.headers.get("Content-Disposition");
      let filename = `${name}_${transaction_id}_${issuer}.pdf`;

      if (disposition && disposition.includes("attachment")) {
        const matches = /filename="([^"]*)"/.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1];
        }
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const handleContainerClick = () => {
    const baseState = {
      transaction_verification_log_id: transaction_id,
      verificationLogId: transaction_id,
      orderId: order_id,
      paymentId: payment_id,
      paymentSignature: payment_signature,
      status: transaction_status,
      paymentStatus: payment_status,
      issuerName: issuer,
      issuerOrganisation: issuer_organisation,
      programmeName: programme_name,
      uploadedDocument: uploaded_document,
      certificateId: certificate_id,
      recipientName: recipient_name,
      recipientEmail: recipient_email,
      comments: comments,
      document_type: document_type,
      verification_cost: verification_cost,
      verification_cost_overall: verification_cost_overall,
      issuer_logo: issuer_logo,
      mode_of_verification: mode_of_verification,
    };

    if (document_type === "Legacy" || document_type === "Data") {
      if (payment_status === "Pending") {
        navigate(`/verifier/legacy/payment-details/${transaction_id}`, {
          state: baseState,
        });
      } else {
        navigate(`/verifier/legacy/summary`, {
          state: baseState,
        });
      }
    } else if (document_type === "OCR") {
      if (payment_status === "Pending") {
        navigate(
          `/verifier/ocr/payment-details/${transaction_id}/${order_id}`,
          {
            state: baseState,
          },
        );
      } else {
        navigate(`/verifier/ocr/summary`, {
          state: baseState,
        });
      }
    }
    // VC document type
    else if (document_type === "VC") {
      if (payment_status === "Pending") {
        navigate(`/verifier/vc/payment-details/${transaction_id}/${order_id}`, {
          state: baseState,
        });
      } else {
        navigate(`/verifier/vc/summary`, {
          state: baseState,
        });
      }
    }
  };

  return (
    <div
      id={`issued-credential-row-${transaction_id}-details-button`}
      className="parent-container flex flex-row items-center border rounded-[var(--cf-issued-credential-border-radius)] py-[var(--cf-issued-credential-template-card-padding-y)] px-6 hover:bg-[#F9FAFB]"
      onClick={handleContainerClick}
    >
      <div className="main-container1 flex items-center gap-2 max-w-[20%] min-w-[20%] mr-5">
        <div className="sub-container1 w-full justify-start">
          <div>
            <p className="font-bold text-lg text-black whitespace-nowrap truncate">
              {name}
            </p>
          </div>
          <div className="sub-container2 flex text-cfIssuedCredentialTemplateCardDateReceiversText">
            {updated_at && (
              <p className="whitespace-nowrap">
                {formatLocalDateTime(updated_at)}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="main-container1 flex items-center gap-2 max-w-[20%] min-w-[20%] mr-5">
        <div className="sub-container1 w-full items-center text-center">
          <p className="line-clamp-1 h-fit font-semibold">{issuer}</p>
        </div>
      </div>

      <div className="main-container2 flex items-center w-[55%] gap-4">
        <div className="flex items-center flex-shrink-0">
          <div className="flex items-center justify-between w-[14.25rem] bg-cfIssuedCredentialTemplateCardDdBG px-3 py-2.5 rounded-[0.25rem]">
            <div>
              <p className="text-cfIssuedCredentialTemplateCardDdText text-cfIssuedCredentialTemplateCardDDText">
                {transaction_id}
              </p>
            </div>
            <div className="relative ml-2">
              {copied ? (
                <img src={correct} alt="" className="w-4 h-4" />
              ) : (
                <img
                  src={Copy}
                  alt=""
                  id={`issued-credential-row-${transaction_id}-copy-button`}
                  className="cursor-pointer"
                  onClick={(e) => handleCopyClick(e, transaction_id)}
                />
              )}
            </div>
          </div>
        </div>

        <div
          className={`flex-shrink-0 ${hasAnyUrls ? "min-w-[10rem] flex-1 flex justify-center" : "flex-1 flex justify-center"}`}
        >
          <div
            id={`issued-credential-row-${transaction_id}-status-badge`}
            className={`w-fit rounded-full text-cfIssuedCredentialTemplateCardStatusText ${
              transaction_status === "Approved"
                ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG"
                : transaction_status === "Expired"
                  ? "bg-cfIssuedCredentialTemplateCardStatusUnderReviewBG"
                  : transaction_status === "Revoked" ||
                      transaction_status === "Rejected"
                    ? "bg-cfIssuedCredentialTemplateCardStatusRevokedBG"
                    : transaction_status === "Payment Pending" ||
                        transaction_status === "Approval Pending"
                      ? "bg-cfIssuedCredentialTemplateCardStatusUnderReviewBG"
                      : "bg-cfIssuedCredentialTemplateCardDescriptionText"
            }`}
          >
            <div className="text-sm text-cfIssuedCredentialTemplateCardStatusText items-center px-[0.625rem] py-[0.125rem] whitespace-nowrap">
              {transaction_status || t("transaction:unavailable")}
            </div>
          </div>
        </div>

        {hasAnyUrls && (
          <div className="sub-container2 flex items-center justify-between gap-4 ml-auto flex-shrink-0 min-w-[15rem]">
            {svg_url ? (
              <button
                type="button"
                id={`issued-credential-row-${transaction_id}-preview-button`}
                aria-label="Preview"
                title="Preview"
                className={`inline-flex items-center justify-center bg-transparent p-0 border-0 ${
                  transaction_status === "Approval Pending" ||
                  transaction_status === "Payment Pending"
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                }`}
                disabled={
                  transaction_status === "Approval Pending" ||
                  transaction_status === "Payment Pending"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    transaction_status !== "Approval Pending" &&
                    transaction_status !== "Payment Pending"
                  ) {
                    openCloseModal(transaction_id);
                  }
                }}
              >
                <img src={PreviewIcon} alt="" className="w-5 h-5" aria-hidden="true" />
              </button>
            ) : (
              <div className="w-5" />
            )}
            {pdf_url ? (
              <button
                id={`issued-credential-row-${transaction_id}-download-button`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    transaction_status !== "Approval Pending" &&
                    transaction_status !== "Payment Pending" &&
                    pdf_url
                  ) {
                    downloadPDF(pdf_url);
                  }
                }}
                style={
                  transaction_status === "Approval Pending" ||
                  transaction_status === "Payment Pending"
                    ? { color: "#878b93", cursor: "not-allowed" }
                    : pdf_url
                      ? { color: "#000000" }
                      : { color: "#878b93", cursor: "not-allowed" }
                }
                className={`px-3 py-2 border-[1.5px] rounded-full text-[0.875rem] hover:no-underline ${
                  transaction_status === "Approval Pending" ||
                  transaction_status === "Payment Pending"
                    ? "opacity-50 border-[#878b93]"
                    : pdf_url
                      ? "hover:text-black border-black"
                      : "border-[#878b93]"
                } `}
              >
                {t("transaction:download")}
              </button>
            ) : (
              <div className="w-[5.5rem]" />
            )}
          </div>
        )}
      </div>
      {isOpen && currentCredentialId === transaction_id && (
        <CredentialPreviewModal
          svg_url={svg_url}
          openCloseModal={openCloseModal}
          isOpen={isOpen}
        />
      )}
    </div>
  );
};

export default IssuedCredentialLongCard;
